import { useGraphQLQuery } from '@betterment/graphql';
import { Accordion, Box, Card, Text } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary, useFeatureFlag } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { EnvelopeAccountPendingOrdersDocument } from './graphql/envelopeAccountPendingOrders.generated';
import { PendingOrderTag } from './PendingOrderTag';

export const PendingOrdersCardView: React.FC = () => {
  const id = useRequiredParam('accountId');
  const { enabled: isPendingOrdersEnabled } = useFeatureFlag({
    splitName: 'retail.self_directed_after_hours_order_cancellation_q4_2025_enabled',
    context: 'pending_orders_card',
  });
  const { data } = useGraphQLQuery(EnvelopeAccountPendingOrdersDocument, { variables: { id }, throwOnError: true });
  const { account } = data || {};
  const pendingOrders = account && 'pendingOrders' in account ? account.pendingOrders : [];

  if (!isPendingOrdersEnabled) return null;
  if (!data) return <CardLoader />;
  if (!pendingOrders.length) return null;

  return (
    <Card>
      <Accordion type="single" collapsible={true}>
        <Accordion.Item value="pending-orders" css={{ border: 'none' }}>
          <Accordion.Trigger
            css={{
              padding: 'space.300',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Text variant="text200" css={{ fontWeight: 'medium' }}>
              Pending orders
            </Text>
            <Box
              className="count-badge"
              css={{
                backgroundColor: 'background.neutral',
                borderRadius: 'round',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto',
              }}
            >
              <Text variant="text50">{pendingOrders.length}</Text>
            </Box>
          </Accordion.Trigger>
          <Accordion.Content css={{ mb: 'space.300' }}>
            <Box
              css={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'space.100',
                px: 'space.300',
              }}
            >
              {pendingOrders.map(order => (
                <PendingOrderTag item={order} key={order.financialSecurityOrderId} />
              ))}
            </Box>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    </Card>
  );
};

export const PendingOrdersCard: React.FC = () => (
  <ErrorBoundary fallbackType="none">
    <PendingOrdersCardView />
  </ErrorBoundary>
);
